import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, interval, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, startWith, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  ContentType,
  DsColors,
  DsPageMessageVariants,
  Icons,
  IconScale,
  IconStyles,
  TableCellData,
  TableCellTextAlign,
  TableColumnConfig,
  TableDropdownItem,
  TableEmptyStateConfig,
  TableRow,
} from '@levelaccess/design-system';

import { Api } from '../../../../../../../shared/constants/api';
import { $scan } from '../../../../../../../shared/constants/scan';
import {
  $discoveredScannableDocument,
  $discoveredScannableDocuments,
  $scannableDocument,
  $scannableDocumentQuery,
  DiscoveredScannableDocumentStatus,
} from '../../../../../../../shared/constants/scannable-document';
import { $sortingOrder } from '../../../../../../../shared/constants/sort';
import { IScan } from '../../../../../../../shared/interfaces/scan.interface';
import {
  IDiscoveredScannableDocument,
  IDiscoveredScannableDocumentResponse,
} from '../../../../../../../shared/interfaces/scannable-document.interface';
import { SharedCommonUtility } from '../../../../../../../shared/utils/common.utility';
import { InfoModalComponent } from '../../../../../components/modals/info-modal/info-modal.component';
import {
  IDclConfig,
  ITableColumn,
  ITableConfig,
  ITableDsPageMessageConfig,
  ITableEmptyState,
  ITableRow,
  SelectAllEvent,
  SortEvent,
  TableEmptyStateType,
} from '../../../../../components/table/ngb-table/utilities/ngb-table.interface';
import { NgbTableUtilities } from '../../../../../components/table/ngb-table/utilities/ngb-table.utilities';
import { DEFAULT_ENTRY_LIMITS } from '../../../../../constants/form.constants';
import { InfoModalOptions } from '../../../../../interfaces/modal-dialog.interface';
import { NotificationPosition } from '../../../../../models/notification.model';
import { ErrorHandlerService } from '../../../../../services/error-handler.service';
import { ModalService } from '../../../../../services/modal.service';
import { NotificationService } from '../../../../../services/notification.service';
import { ScannableDocumentService } from '../../../../../services/scannable-document.service';
import { TranslateService } from '../../../../../translate/translate.service';
import { DateUtility } from '../../../../../utility/date.utility';
import { PULLING_INTERVAL } from '../../../../../shared/constants';
import { ScanService } from '../../../../../services/scan.service';
import { ScanOrigin } from '../../../../../../../shared/constants/scanning';
import { UserAclService } from '../../../../../services/user-acl.service';
import { AclSecurityAdapter } from '../../../../../services/acl.service';
import { RequiredSecurities } from '../../../../../../../shared/constants/required-securities';
import { FunctionalArea, SecureAction } from '../../../../../../../shared/constants/security';
import { IDropdownItem } from '../../../../../components/table/ngb-table/cells/dropdown-cell/dropdown-cell.component';
import { ICellConfig } from '../../../../../components/table/ngb-table/cells/base-cell/base-cell.component';
import { FeatureFlagService } from '../../../../../services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../../../../../shared/interfaces/feature-flag.interface';

enum DiscoveredScannableDocumentTableColumns {
  url = 'url',
  status = 'status',
  fileSize = 'fileSize',
  actions = 'actions',
}

type DsTableColumnStyles = NonNullable<TableColumnConfig['styles']>;

interface IDsTableConfig {
  columns: TableColumnConfig[];
  tableCaption: string;
  emptyState: TableEmptyStateConfig;
}

interface IDocumentActionItem {
  label: string;
  disabled: boolean;
  onSelect: () => void;
}

const discoveredScannableDocumentStatusLabel: Record<DiscoveredScannableDocumentStatus, string> = {
  [DiscoveredScannableDocumentStatus.new]: 'label_scannable_document_status_new',
  [DiscoveredScannableDocumentStatus.downloading]: 'label_scannable_document_status_downloading',
  [DiscoveredScannableDocumentStatus.download_error]: 'label_scannable_document_status_download_error',
  [DiscoveredScannableDocumentStatus.submitted_for_testing]: 'label_uploadedAt',
};

const dsTableColumnStyles: Partial<Record<DiscoveredScannableDocumentTableColumns, DsTableColumnStyles>> = {
  [DiscoveredScannableDocumentTableColumns.url]: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: '12rem',
    width: '55%',
  },
  [DiscoveredScannableDocumentTableColumns.status]: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '30%',
    minWidth: '9rem',
  },
  [DiscoveredScannableDocumentTableColumns.fileSize]: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '15%',
    minWidth: '6rem',
  },
};

const dsTableCellStyles: Partial<Record<DiscoveredScannableDocumentTableColumns, DsTableColumnStyles>> = {
  [DiscoveredScannableDocumentTableColumns.url]: {
    ...dsTableColumnStyles[DiscoveredScannableDocumentTableColumns.url],
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-line',
  },
  [DiscoveredScannableDocumentTableColumns.status]: {
    ...dsTableColumnStyles[DiscoveredScannableDocumentTableColumns.status],
    whiteSpace: 'pre-line',
  },
};

@Component({
  selector: 'app-view-scan-results-scannable-documents',
  templateUrl: './view-scan-results-scannable-documents.component.html',
  styleUrls: ['./view-scan-results-scannable-documents.component.scss'],
  standalone: false,
})
export class ViewScanResultsScannableDocumentsComponent implements OnInit, OnDestroy, OnChanges {
  private subscriptions: Subscription;

  private sortOption$: BehaviorSubject<SortEvent>;
  private selectedDiscoveredScannableDocumentsMap: Map<string, IDiscoveredScannableDocument>;
  private discoveredScannableDocumentsOnPage: IDiscoveredScannableDocument[];
  private refreshDocumentObservablePipeline: BehaviorSubject<void>;
  private aclSecurityAdapter: AclSecurityAdapter;
  private useDsPageMessage: boolean;

  public pageSizes: number[];

  public page$: BehaviorSubject<number>;
  public pageSize$: BehaviorSubject<number>;
  public totalRows$: Observable<number>;
  public tableData$: Observable<ITableRow[]>;
  public sortOrder$: Observable<Record<string, $sortingOrder>>;
  protected dsTableData$: Observable<TableRow[]>;
  protected dsTableConfig: IDsTableConfig;
  protected useDsTable: boolean;

  public discoveredScannableDocumentsResponse$: BehaviorSubject<IDiscoveredScannableDocumentResponse>;
  public discoveredScannableDocuments: IDiscoveredScannableDocument[];

  public rowSelectionIndex: number;
  public allPageSelected$: Observable<boolean>;
  public selectionChanged$: Subject<void>;

  public statusUpdatePollingSubscription: Subscription;
  public isMonitoringScan: boolean;

  @Input() public scan: IScan;

  constructor(
    private translateService: TranslateService,
    private scannableDocumentService: ScannableDocumentService,
    private notificationService: NotificationService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private errorHandlerService: ErrorHandlerService,
    private modalService: ModalService,
    private scanService: ScanService,
    private userAclService: UserAclService,
    private featureFlagService: FeatureFlagService,
  ) {
    this.subscriptions = new Subscription();
    this.statusUpdatePollingSubscription = new Subscription();

    this.pageSizes = DEFAULT_ENTRY_LIMITS as number[];

    this.page$ = new BehaviorSubject<number>(1);
    this.pageSize$ = new BehaviorSubject<number>(this.pageSizes[this.pageSizes.length - 1]);
    this.sortOption$ = new BehaviorSubject<SortEvent>(null);

    this.discoveredScannableDocumentsResponse$ = new BehaviorSubject<IDiscoveredScannableDocumentResponse>(null);
    this.refreshDocumentObservablePipeline = new BehaviorSubject<void>(null);

    this.rowSelectionIndex = -1;
    this.selectionChanged$ = new Subject<void>();
    this.selectedDiscoveredScannableDocumentsMap = new Map<string, IDiscoveredScannableDocument>();
    this.discoveredScannableDocumentsOnPage = [];
    this.useDsPageMessage = this.featureFlagService.variation(FeatureFlagCollection.dsPageMessage, false);
    this.useDsTable = this.featureFlagService.variation(FeatureFlagCollection.dsTable, false);
    if (this.useDsTable) {
      this.dsTableConfig = this.buildDsTableConfig();
    }
  }

  private getDiscoveredOnText(discoveredScannableDocument: IDiscoveredScannableDocument): string {
    const discoveredOnCount: number = discoveredScannableDocument[$discoveredScannableDocument.foundInPages].length;
    return this.translateService.instant('discovered_on_webpages', discoveredOnCount);
  }

  private getUrlCellContent(discoveredScannableDocument: IDiscoveredScannableDocument): string {
    return `<div class="url-cell">
      <span class="url">${discoveredScannableDocument[$discoveredScannableDocument.url]}</span>
      <br/>
      <span class="discovered-on">${this.getDiscoveredOnText(discoveredScannableDocument)}</span>
    </div>`;
  }

  private getDsUrlCellValue(discoveredScannableDocument: IDiscoveredScannableDocument): string {
    return `${discoveredScannableDocument[$discoveredScannableDocument.url]}\n${this.getDiscoveredOnText(discoveredScannableDocument)}`;
  }

  private canTestDocument(discoveredScannableDocument: IDiscoveredScannableDocument): boolean {
    return (
      this.isDownloading(discoveredScannableDocument) === false &&
      this.isSubmittedForTesting(discoveredScannableDocument) === false
    );
  }

  private canShowUrls(discoveredScannableDocument: IDiscoveredScannableDocument): boolean {
    return discoveredScannableDocument[$discoveredScannableDocument.foundInPages].length > 0;
  }

  private showUrlsList(discoveredScannableDocument: IDiscoveredScannableDocument): void {
    const redirectPath: string = this.isMonitoringScan
      ? `/${Api.monitoring}/${Api.pdf}/${Api.pdfDiscovered}/${this.scan[$scan._id]}`
      : `/${Api.scans}/${Api.scan_results}/${this.scan[$scan._id]}/${Api.single_report}/${Api.scannableDocument}`;
    this.router
      .navigate([redirectPath], {
        relativeTo: this.activatedRoute,
        queryParams: {
          [$scannableDocumentQuery.documentUrl]: discoveredScannableDocument[$discoveredScannableDocument.url],
        },
      })
      .catch(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService));
  }

  private getDocumentActionItems(discoveredScannableDocument: IDiscoveredScannableDocument): IDocumentActionItem[] {
    const hasPDFTestinCreatePermissions: boolean = this.aclSecurityAdapter
      .useWorkspaceFromUser()
      .useFunctionalActions(RequiredSecurities.PDF_Testing_Create.functionalActions)
      .check();

    const testScannableDocument: IDocumentActionItem = {
      label: this.translateService.instant(`scannable_document_run_scan`),
      onSelect: (): void => this.downloadDiscoveredScannableDocument(discoveredScannableDocument),
      disabled: !this.canTestDocument(discoveredScannableDocument),
    };

    const hasPDFViewTestResultPermissions: boolean = this.aclSecurityAdapter
      .useWorkspaceFromUser()
      .useDigitalPropertyFromUser()
      .useFunctionalActions({ [FunctionalArea.pdf_testing]: [SecureAction.read] })
      .check();

    const viewTestReport: IDocumentActionItem = {
      label: this.translateService.instant(`scannable_document_see_all_urls`),
      onSelect: (): void => this.showUrlsList(discoveredScannableDocument),
      disabled: !this.canShowUrls(discoveredScannableDocument),
    };

    return [
      ...(hasPDFTestinCreatePermissions ? [testScannableDocument] : []),
      ...(hasPDFViewTestResultPermissions ? [viewTestReport] : []),
    ];
  }

  private getActionCell(discoveredScannableDocument: IDiscoveredScannableDocument): IDclConfig<ICellConfig> {
    const dropdownItems: IDropdownItem[] = this.getDocumentActionItems(discoveredScannableDocument).map(
      (action: IDocumentActionItem): IDropdownItem => ({
        label: action.label,
        clickListener: action.onSelect,
        disabled: action.disabled,
      }),
    );

    return NgbTableUtilities.dropdownCell({
      text: '',
      classes: ['btn-link', 'px-0'],
      dropdownItems: dropdownItems,
    });
  }

  private getDsDropdownItems(discoveredScannableDocument: IDiscoveredScannableDocument): TableDropdownItem[] {
    return this.getDocumentActionItems(discoveredScannableDocument).map((action: IDocumentActionItem): TableDropdownItem => ({
      label: action.label,
      disabled: action.disabled,
      onSelect: action.onSelect,
    }));
  }

  private isSubmittedForTesting(discoveredScannableDocument: IDiscoveredScannableDocument): boolean {
    return (
      discoveredScannableDocument[$discoveredScannableDocument.status] === DiscoveredScannableDocumentStatus.submitted_for_testing
    );
  }

  private isDownloading(discoveredScannableDocument: IDiscoveredScannableDocument): boolean {
    return discoveredScannableDocument[$discoveredScannableDocument.status] === DiscoveredScannableDocumentStatus.downloading;
  }

  private isDownloadError(discoveredScannableDocument: IDiscoveredScannableDocument): boolean {
    return discoveredScannableDocument[$discoveredScannableDocument.status] === DiscoveredScannableDocumentStatus.download_error;
  }

  private getStatusLabel(discoveredScannableDocument: IDiscoveredScannableDocument): string {
    return this.translateService.instant(
      discoveredScannableDocumentStatusLabel[discoveredScannableDocument[$discoveredScannableDocument.status]],
    );
  }

  private getSubmittedForTestingLabel(discoveredScannableDocument: IDiscoveredScannableDocument): string {
    const formattedDateString: string = DateUtility.formatCellDate(
      new Date(discoveredScannableDocument[$discoveredScannableDocument.uploadedAt]),
    );

    return `${this.getStatusLabel(discoveredScannableDocument)} ${formattedDateString}`;
  }

  private getStatusCell(discoveredScannableDocument: IDiscoveredScannableDocument): IDclConfig<ICellConfig> {
    const statusLabel: string = this.getStatusLabel(discoveredScannableDocument);

    if (this.isDownloading(discoveredScannableDocument)) {
      return NgbTableUtilities.pendingCell({
        text: statusLabel,
      });
    }

    if (this.isDownloadError(discoveredScannableDocument)) {
      const errorMessage: string = discoveredScannableDocument[$discoveredScannableDocument.message] ?? '';
      const errorStatusHTML: string = `
      <span class="download-error">
        ${statusLabel}
        <br/>
        ${errorMessage}
      </span>
      `;
      return NgbTableUtilities.htmlCell({
        text: errorStatusHTML,
      });
    }

    if (this.isSubmittedForTesting(discoveredScannableDocument)) {
      return NgbTableUtilities.textCell({
        text: this.getSubmittedForTestingLabel(discoveredScannableDocument),
      });
    }

    return NgbTableUtilities.textCell({
      text: statusLabel,
    });
  }

  private getDsStatusLabel(discoveredScannableDocument: IDiscoveredScannableDocument): string {
    if (this.isSubmittedForTesting(discoveredScannableDocument)) {
      return this.getSubmittedForTestingLabel(discoveredScannableDocument);
    }

    const statusLabel: string = this.getStatusLabel(discoveredScannableDocument);
    if (!this.isDownloadError(discoveredScannableDocument)) {
      return statusLabel;
    }

    const errorMessage: string = discoveredScannableDocument[$discoveredScannableDocument.message] ?? '';
    return SharedCommonUtility.notNullishOrEmpty(errorMessage) ? `${statusLabel}\n${errorMessage}` : statusLabel;
  }

  private getDsStatusCell(discoveredScannableDocument: IDiscoveredScannableDocument): TableCellData {
    return {
      value: this.getDsStatusLabel(discoveredScannableDocument),
      styles: dsTableCellStyles[DiscoveredScannableDocumentTableColumns.status],
    };
  }

  private getTableData(discoveredScannableDocumentsData: IDiscoveredScannableDocument[]): ITableRow[] {
    return discoveredScannableDocumentsData.map((discoveredScannableDocument: IDiscoveredScannableDocument) => {
      return {
        originalData: discoveredScannableDocument,
        data: {
          [$discoveredScannableDocument.url]: NgbTableUtilities.htmlCell({
            text: this.getUrlCellContent(discoveredScannableDocument),
          }),
          [$discoveredScannableDocument.status]: this.getStatusCell(discoveredScannableDocument),
          [$discoveredScannableDocument.fileSize]: NgbTableUtilities.textCell({
            text: discoveredScannableDocument[$discoveredScannableDocument.fileSize]
              ? SharedCommonUtility.formatBytes(discoveredScannableDocument[$discoveredScannableDocument.fileSize])
              : '',
          }),
          actions: this.getActionCell(discoveredScannableDocument),
        },
      };
    });
  }

  private getDsTableData(discoveredScannableDocumentsData: IDiscoveredScannableDocument[]): TableRow[] {
    return discoveredScannableDocumentsData.map((discoveredScannableDocument: IDiscoveredScannableDocument): TableRow => {
      return {
        key: discoveredScannableDocument[$discoveredScannableDocument.url],
        originalData: {
          [$discoveredScannableDocument.url]: discoveredScannableDocument[$discoveredScannableDocument.url],
        },
        data: {
          [DiscoveredScannableDocumentTableColumns.url]: {
            value: this.getDsUrlCellValue(discoveredScannableDocument),
            styles: dsTableCellStyles[DiscoveredScannableDocumentTableColumns.url],
          },
          [DiscoveredScannableDocumentTableColumns.status]: this.getDsStatusCell(discoveredScannableDocument),
          [DiscoveredScannableDocumentTableColumns.fileSize]: {
            value: discoveredScannableDocument[$discoveredScannableDocument.fileSize]
              ? SharedCommonUtility.formatBytes(discoveredScannableDocument[$discoveredScannableDocument.fileSize])
              : '',
          },
          [DiscoveredScannableDocumentTableColumns.actions]: {
            dropdown: {
              toggle: {
                ariaLabel: this.translateService.instant(
                  'chat_actions_for',
                  discoveredScannableDocument[$discoveredScannableDocument.url],
                ),
              },
              items: this.getDsDropdownItems(discoveredScannableDocument),
            },
          },
        } satisfies Record<DiscoveredScannableDocumentTableColumns, TableCellData>,
      };
    });
  }

  private showInfoModal(): void {
    const infoModalOptions: InfoModalOptions = {
      headerKey: 'scannable_document_added_to_inventory',
      bodyHeader: 'scannable_document_added_to_inventory_info',
    };
    const modal: InfoModalComponent = this.modalService.open(InfoModalComponent).instance;
    modal.init(infoModalOptions);
  }

  private onDownloadDiscoveredScannableDocumentSuccess(discoveredScannableDocument: IDiscoveredScannableDocument): void {
    discoveredScannableDocument.status = DiscoveredScannableDocumentStatus.downloading;

    this.showInfoModal();

    this.notificationService.success(
      this.translateService.instant('action_test_this_document_success'),
      NotificationPosition.Toast,
      false,
    );

    this.refreshDocumentObservablePipeline.next();
  }

  private onDownloadDiscoveredScannableDocumentError(
    discoveredScannableDocument: IDiscoveredScannableDocument,
    error: any,
  ): void {
    console.error(
      '[ViewScanResultsPagesNotScannedComponent.onDownloadDiscoveredScannableDocumentError]',
      discoveredScannableDocument,
      error,
    );

    discoveredScannableDocument.status = DiscoveredScannableDocumentStatus.download_error;

    this.notificationService.error(this.translateService.instant('action_test_this_document_error'), NotificationPosition.Toast);
  }

  private downloadDiscoveredScannableDocument(discoveredScannableDocument: IDiscoveredScannableDocument): void {
    this.subscriptions.add(
      this.scannableDocumentService
        .createScannableDocumentFromUrls([discoveredScannableDocument[$discoveredScannableDocument.url]], this.scan._id)
        .subscribe({
          next: this.onDownloadDiscoveredScannableDocumentSuccess.bind(this, discoveredScannableDocument),
          error: this.onDownloadDiscoveredScannableDocumentError.bind(this, discoveredScannableDocument),
        }),
    );
  }

  private setupObservables(): void {
    this.subscriptions.add(
      combineLatest([
        this.page$.pipe(distinctUntilChanged()),
        this.pageSize$.pipe(distinctUntilChanged()),
        this.refreshDocumentObservablePipeline,
      ])
        .pipe(
          switchMap(([page, pageSize, _]: [number, number, void]): Observable<IDiscoveredScannableDocumentResponse> => {
            const start: number = (page - 1) * pageSize;
            const end: number = page * pageSize;
            return of({
              [$discoveredScannableDocuments.discoveredDocuments]: this.scan?.scannableDocuments?.slice(start, end) ?? [],
              [$discoveredScannableDocuments._total]: this.scan?.scannableDocuments?.length ?? 0,
            });
          }),
        )
        .subscribe(this.discoveredScannableDocumentsResponse$),
    );

    this.totalRows$ = this.discoveredScannableDocumentsResponse$.pipe(
      filter((data: IDiscoveredScannableDocumentResponse): boolean => SharedCommonUtility.notNullish(data)),
      map((data: IDiscoveredScannableDocumentResponse): number => {
        return data._total;
      }),
      distinctUntilChanged(),
    );

    if (this.useDsTable) {
      this.dsTableData$ = this.discoveredScannableDocumentsResponse$.pipe(
        filter((data: IDiscoveredScannableDocumentResponse): boolean => SharedCommonUtility.notNullish(data)),
        map((data: IDiscoveredScannableDocumentResponse): TableRow[] =>
          this.getDsTableData(data[$discoveredScannableDocuments.discoveredDocuments]),
        ),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    } else {
      this.tableData$ = this.discoveredScannableDocumentsResponse$.pipe(
        map((data: IDiscoveredScannableDocumentResponse): ITableRow[] => {
          return this.getTableData(data[$discoveredScannableDocuments.discoveredDocuments]);
        }),
      );

      this.sortOrder$ = this.sortOption$.pipe(
        map((sortOption: SortEvent): Record<string, $sortingOrder> => {
          return sortOption ? { [sortOption.column]: sortOption.direction } : {};
        }),
      );

      this.allPageSelected$ = this.selectionChanged$.pipe(
        switchMap((): Observable<boolean> => {
          const scannableDocumentIds: string[] = this.discoveredScannableDocumentsOnPage.map(
            (scannableDocument: IDiscoveredScannableDocument): string => scannableDocument[$scannableDocument._id],
          );

          const allSelected: boolean =
            scannableDocumentIds.length > 0 &&
            scannableDocumentIds.every((id: string): boolean => this.selectedDiscoveredScannableDocumentsMap.has(id));
          return of(allSelected);
        }),
      );
    }
  }

  private setupDiscoveredDocumentsPollingObservable(): void {
    const getDownloadingDocuments = ([_, discoveredDocuments]: [
      number,
      IDiscoveredScannableDocumentResponse,
    ]): IDiscoveredScannableDocument[] =>
      discoveredDocuments[$discoveredScannableDocuments.discoveredDocuments].filter(
        (document: IDiscoveredScannableDocument): boolean =>
          document[$discoveredScannableDocument.status] === DiscoveredScannableDocumentStatus.downloading,
      );

    const documentsWithStatusesToWatch$: Observable<IDiscoveredScannableDocument[]> = interval(PULLING_INTERVAL.SLOW).pipe(
      withLatestFrom(this.discoveredScannableDocumentsResponse$),
      map(getDownloadingDocuments),
      filter((documents: IDiscoveredScannableDocument[]): boolean => documents.length !== 0),
    );

    const mergeDocuments = ([refreshedDocuments, oldDocumentsResponse]: [
      IDiscoveredScannableDocument[],
      IDiscoveredScannableDocumentResponse,
    ]): IDiscoveredScannableDocumentResponse => {
      const mergedDocuments: IDiscoveredScannableDocumentResponse = { ...oldDocumentsResponse };
      refreshedDocuments?.forEach((refreshedDocument: IDiscoveredScannableDocument): void => {
        const documentIndex = mergedDocuments[$discoveredScannableDocuments.discoveredDocuments]?.findIndex(
          (oldDocument: IDiscoveredScannableDocument): boolean =>
            oldDocument[$discoveredScannableDocument.url] === refreshedDocument[$discoveredScannableDocument.url],
        );
        if (documentIndex > -1) {
          const oldDocument: IDiscoveredScannableDocument =
            mergedDocuments[$discoveredScannableDocuments.discoveredDocuments][documentIndex];
          const documentOldState: DiscoveredScannableDocumentStatus = oldDocument[$discoveredScannableDocument.status];

          if (documentOldState === DiscoveredScannableDocumentStatus.downloading) {
            mergedDocuments[$discoveredScannableDocuments.discoveredDocuments][documentIndex] = refreshedDocument;
          }
        }
      });
      return mergedDocuments;
    };

    const loadDocumentsFromScan = (): Observable<IDiscoveredScannableDocument[]> =>
      this.scanService.getScanDiscoveredDocuments(this.scan._id).pipe(
        map(
          (response: IDiscoveredScannableDocumentResponse): IDiscoveredScannableDocument[] =>
            response[$discoveredScannableDocuments.discoveredDocuments],
        ),
        catchError(() => {
          return of([]);
        }),
      );

    this.statusUpdatePollingSubscription = documentsWithStatusesToWatch$
      .pipe(switchMap(loadDocumentsFromScan), withLatestFrom(this.discoveredScannableDocumentsResponse$), map(mergeDocuments))
      .subscribe(this.discoveredScannableDocumentsResponse$);
    this.subscriptions.add(
      this.userAclService.createCheckAccessForCurrentUser().subscribe((adapter: AclSecurityAdapter) => {
        this.aclSecurityAdapter = adapter;
      }),
    );
  }

  public get tableConfig(): ITableConfig {
    const columns: Record<string, ITableColumn> = {
      [$discoveredScannableDocument.url]: {
        translationKey: 'table_column_url',
        styles: {
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          maxWidth: '230px',
          minWidth: '230px',
        },
      },
      [$discoveredScannableDocument.status]: {
        translationKey: 'status',
        styles: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          verticalAlign: 'middle',
          width: '20%',
          minWidth: '230px',
        },
      },
      [$discoveredScannableDocument.fileSize]: {
        translationKey: 'table_column_file_size',
        styles: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          verticalAlign: 'middle',
          width: '10%',
        },
      },
      actions: {
        translationKey: 'table_column_actions',
      },
    };

    return {
      columns: columns,
      caption: this.translateService.instant('scannable_documents_discovered'),
      emptyState: this.getEmptyState(),
    };
  }

  private buildDsTableConfig(): IDsTableConfig {
    const columns: TableColumnConfig[] = [
      {
        key: DiscoveredScannableDocumentTableColumns.url,
        header: this.translateService.instant('table_column_url'),
        contentType: ContentType.string,
        textAlign: TableCellTextAlign.left,
        styles: dsTableColumnStyles[DiscoveredScannableDocumentTableColumns.url],
      },
      {
        key: DiscoveredScannableDocumentTableColumns.status,
        header: this.translateService.instant('status'),
        contentType: ContentType.string,
        textAlign: TableCellTextAlign.left,
        styles: dsTableColumnStyles[DiscoveredScannableDocumentTableColumns.status],
      },
      {
        key: DiscoveredScannableDocumentTableColumns.fileSize,
        header: this.translateService.instant('table_column_file_size'),
        contentType: ContentType.string,
        textAlign: TableCellTextAlign.left,
        styles: dsTableColumnStyles[DiscoveredScannableDocumentTableColumns.fileSize],
      },
      {
        key: DiscoveredScannableDocumentTableColumns.actions,
        header: this.translateService.instant('table_column_actions'),
        contentType: ContentType.dropdown,
        textAlign: TableCellTextAlign.center,
      },
    ];

    return {
      columns,
      tableCaption: this.translateService.instant('scannable_documents_discovered'),
      emptyState: this.buildDsTableEmptyState(),
    };
  }

  private buildDsTableEmptyState(): TableEmptyStateConfig {
    return {
      icon: Icons.NotFoundBrand,
      iconColor: DsColors.Primary,
      iconScale: IconScale.FA_4X,
      iconStyle: IconStyles.Light,
      label: this.translateService.instant('label_no_scannable_documents'),
      body: this.translateService.instant('no_scannable_document'),
    };
  }

  private getEmptyState(): ITableEmptyState {
    const title: string = this.translateService.instant('label_no_scannable_documents');
    const subtitle: string = this.translateService.instant('no_scannable_document');

    if (!this.useDsPageMessage) {
      return {
        title,
        subtitle,
      };
    }

    const dsPageMessage: ITableDsPageMessageConfig = {
      icon: Icons.NotFoundBrand,
      iconColor: DsColors.Primary,
      iconScale: IconScale.FA_4X,
      iconStyle: IconStyles.Light,
      variant: DsPageMessageVariants.info,
    };

    return {
      type: TableEmptyStateType.dsPageMessage,
      title,
      subtitle,
      dsPageMessage,
    };
  }

  public onSort(sort: SortEvent): void {
    this.sortOption$.next(sort);
  }

  public onPageSizeChange(pageSize: number): void {
    this.pageSize$.next(pageSize);
  }

  public onPageChange(page: number): void {
    this.page$.next(page);
  }

  public shouldShowComponent(): boolean {
    return this.discoveredScannableDocuments?.length > 0 && !this.isMonitoringScan;
  }

  public onSelectAll(event: SelectAllEvent): void {
    this.discoveredScannableDocumentsOnPage.forEach((discoveredScannableDocument: IDiscoveredScannableDocument) => {
      if (event.selected) {
        this.selectedDiscoveredScannableDocumentsMap.set(
          discoveredScannableDocument[$scannableDocument._id],
          discoveredScannableDocument,
        );
      } else {
        this.selectedDiscoveredScannableDocumentsMap.delete(discoveredScannableDocument[$scannableDocument._id]);
      }
    });

    this.selectionChanged$.next();

    this.tableData$ = this.discoveredScannableDocumentsResponse$.pipe(
      map((data: IDiscoveredScannableDocumentResponse): ITableRow[] => {
        return this.getTableData(data[$discoveredScannableDocuments.discoveredDocuments]);
      }),
    );
  }

  public ngOnInit(): void {
    this.setupObservables();
    this.setupDiscoveredDocumentsPollingObservable();
    this.subscriptions.add(
      this.featureFlagService
        .variation$(FeatureFlagCollection.dsPageMessage, false)
        .pipe(map(Boolean), startWith(this.useDsPageMessage))
        .subscribe((useDsPageMessage: boolean): void => {
          this.useDsPageMessage = useDsPageMessage;
        }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.statusUpdatePollingSubscription.unsubscribe();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.scan.previousValue !== changes.scan.currentValue && SharedCommonUtility.notNullish(changes.scan.currentValue)) {
      this.discoveredScannableDocuments = changes.scan.currentValue?.[$scan.scannableDocuments];
      this.isMonitoringScan = changes.scan.currentValue?.[$scan.scanOrigin] === ScanOrigin.monitoring;
      this.refreshDocumentObservablePipeline.next();
    }
  }
}
